// ═══════════════════════════════════════════════════════════════════════════════
// EMAIL PREVIEW — Professional Technical Response Email Viewer
// EnergiPro Solutions — Upgraded Communication System v1.0
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from 'react';
import {
  Mail, Copy, CheckCircle2, Eye, Code, FileText,
  Send, ExternalLink, ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';
import { RFQState } from './rfqConfig';
import { RuleEngineOutput } from './ruleEngine';
import {
  generateEmailTemplate,
  renderEmailAsPlainText,
  renderEmailAsHTML,
  EmailTemplate,
  EmailSection,
} from './emailTemplateGenerator';

// ─── PROPS ───────────────────────────────────────────────────────────────────

interface EmailPreviewProps {
  rfqState: RFQState;
  ruleOutput: RuleEngineOutput;
  referenceId: string;
}

// ─── SECTION CARD ────────────────────────────────────────────────────────────

const SectionCard: React.FC<{ section: EmailSection; index: number }> = ({ section, index }) => {
  const [expanded, setExpanded] = useState(true);

  const typeStyles: Record<string, { border: string; bg: string; icon: string; label: string }> = {
    opening: { border: 'border-slate-600/50', bg: 'bg-slate-800/30', icon: '💼', label: 'Opening' },
    understanding: { border: 'border-orange-500/30', bg: 'bg-orange-500/5', icon: '🎯', label: 'Understanding' },
    solution: { border: 'border-blue-500/30', bg: 'bg-blue-500/5', icon: '⚙️', label: 'Solution' },
    attachments: { border: 'border-slate-600/50', bg: 'bg-slate-800/20', icon: '📎', label: 'Attachments' },
    assumptions: { border: 'border-amber-500/30', bg: 'bg-amber-500/5', icon: '⚠️', label: 'Assumptions' },
    cta: { border: 'border-green-500/30', bg: 'bg-green-500/5', icon: '🚀', label: 'Call to Action' },
    close: { border: 'border-slate-600/50', bg: 'bg-slate-800/20', icon: '✅', label: 'Closing' },
  };

  const style = typeStyles[section.type] || typeStyles.opening;

  return (
    <div className={`border ${style.border} ${style.bg} rounded-lg overflow-hidden transition-all`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-base">{style.icon}</span>
          <span className="text-xs font-medium text-slate-300 uppercase tracking-wider">{style.label}</span>
          {section.type === 'cta' && (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-green-500/20 text-green-300 border border-green-500/30">
              High Impact
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
            {section.content}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export const EmailPreview: React.FC<EmailPreviewProps> = ({ rfqState, ruleOutput, referenceId }) => {
  const [viewMode, setViewMode] = useState<'structured' | 'plaintext' | 'html'>('structured');
  const [copied, setCopied] = useState(false);

  // Generate email template
  const emailTemplate: EmailTemplate = useMemo(
    () => generateEmailTemplate(rfqState, ruleOutput, referenceId),
    [rfqState, ruleOutput, referenceId]
  );

  const plainText = useMemo(() => renderEmailAsPlainText(emailTemplate), [emailTemplate]);
  const htmlContent = useMemo(() => renderEmailAsHTML(emailTemplate), [emailTemplate]);

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      const textToCopy = viewMode === 'html' ? htmlContent : plainText;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="bg-[#0a0f1a] border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 border-b border-slate-700/50 px-5 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
              <Mail className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <h3 className="font-['Oswald'] text-sm text-white uppercase tracking-wider">Client Response Email</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Upgraded technical response format</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[10px] text-orange-300 font-medium uppercase tracking-wider">Auto-Generated</span>
          </div>
        </div>
      </div>

      {/* Subject Line Preview */}
      <div className="px-5 py-3 bg-slate-900/50 border-b border-slate-700/30">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Subject:</span>
          <span className="text-sm text-white font-medium">{emailTemplate.subject}</span>
        </div>
        <div className="flex items-center gap-4 mt-1.5">
          <span className="text-[10px] text-slate-500">
            To: <span className="text-slate-400">{rfqState.email || 'client@company.com'}</span>
          </span>
          <span className="text-[10px] text-slate-500">
            From: <span className="text-slate-400">operations@energipro.com</span>
          </span>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-700/30">
        <div className="flex gap-1">
          <button
            onClick={() => setViewMode('structured')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              viewMode === 'structured'
                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Eye className="w-3 h-3" />
            Structured
          </button>
          <button
            onClick={() => setViewMode('plaintext')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              viewMode === 'plaintext'
                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <FileText className="w-3 h-3" />
            Plain Text
          </button>
          <button
            onClick={() => setViewMode('html')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              viewMode === 'html'
                ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            <Code className="w-3 h-3" />
            HTML
          </button>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
        >
          {copied ? (
            <>
              <CheckCircle2 className="w-3 h-3 text-green-400" />
              <span className="text-green-400">Copied</span>
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* Content Area */}
      <div className="p-5 max-h-[600px] overflow-y-auto">
        {viewMode === 'structured' && (
          <div className="space-y-3">
            {emailTemplate.sections.map((section, idx) => (
              <SectionCard key={section.id} section={section} index={idx} />
            ))}
          </div>
        )}

        {viewMode === 'plaintext' && (
          <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap leading-relaxed bg-slate-900/50 rounded-lg p-4 border border-slate-700/30">
            {plainText}
          </pre>
        )}

        {viewMode === 'html' && (
          <div className="space-y-4">
            <div className="bg-white rounded-lg overflow-hidden border border-slate-600/30">
              <iframe
                srcDoc={htmlContent}
                title="Email Preview"
                className="w-full h-[500px] border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-5 py-4 border-t border-slate-700/30 bg-slate-900/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-500 font-mono">{emailTemplate.metadata.referenceId}</span>
            <span className="text-[10px] text-slate-600">•</span>
            <span className="text-[10px] text-slate-500">
              {new Date(emailTemplate.metadata.generatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={emailTemplate.metadata.portalLink}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Portal Link
            </a>
            <button className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-wider bg-orange-600 hover:bg-orange-500 text-white transition-colors">
              <Send className="w-3.5 h-3.5" />
              Send Email
            </button>
          </div>
        </div>
      </div>

      {/* Strategy Notes */}
      <div className="px-5 py-3 bg-slate-950 border-t border-slate-700/30">
        <div className="flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-orange-400 mt-0.5 flex-shrink-0" />
          <div className="text-[10px] text-slate-500 leading-relaxed">
            <span className="text-orange-400 font-medium">Communication Strategy:</span>{' '}
            Shows understanding → builds trust · Shows solution → builds confidence · Shows structure → builds credibility · Pushes action → drives response
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailPreview;
